// apps/workflow/src/pages/admin/AdminPayments.jsx
import React, { useState, useEffect } from 'react';
import { DollarSign, RefreshCw, CheckCircle, Clock, Filter } from 'lucide-react';
import { toast } from 'sonner';
import pb from '../../lib/pocketbaseClient';

export default function AdminPayments() {
  const [earnings, setEarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');

  const fetchEarnings = async () => {
    setLoading(true);
    try {
      const records = await pb.collection('designer_earnings').getFullList({
        sort: '-weekStart',
        expand: 'designerId,taskId,projectId',
      });
      setEarnings(records);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load earnings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEarnings(); }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchEarnings();
    setRefreshing(false);
    toast.success('Refreshed');
  };

  const handleMarkPaid = async (id) => {
    try {
      await pb.collection('designer_earnings').update(id, {
        status: 'paid',
        paidDate: new Date().toISOString(),
      });
      toast.success('Marked as paid');
      fetchEarnings();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const filteredEarnings = filterStatus === 'all' 
    ? earnings 
    : earnings.filter(e => e.status === filterStatus);

  const totalPending = earnings.filter(e => e.status === 'pending').length;
  const totalPaid = earnings.filter(e => e.status === 'paid').length;

  if (loading) {
    return <div className="flex justify-center items-center h-64"><div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" /></div>;
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <DollarSign className="w-6 h-6 text-amber-500" />
          Freelancer Payment Management
        </h1>
        <button onClick={handleRefresh} disabled={refreshing} className="p-2 text-slate-400 hover:text-amber-600 transition rounded-lg hover:bg-amber-50 disabled:opacity-50">
          <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
          <p className="text-sm text-indigo-600 font-medium">Total Entries</p>
          <p className="text-2xl font-bold text-indigo-700">{earnings.length}</p>
        </div>
        <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
          <p className="text-sm text-amber-600 font-medium">Pending</p>
          <p className="text-2xl font-bold text-amber-700">{totalPending}</p>
        </div>
        <div className="bg-green-50 rounded-xl p-4 border border-green-200">
          <p className="text-sm text-green-600 font-medium">Paid</p>
          <p className="text-2xl font-bold text-green-700">{totalPaid}</p>
        </div>
        <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
          <p className="text-sm text-purple-600 font-medium">Total Amount</p>
          <p className="text-2xl font-bold text-purple-700">₹{earnings.reduce((s, e) => s + (e.amount || 0), 0).toFixed(2)}</p>
        </div>
      </div>

      <div className="flex items-center gap-4 bg-white p-4 rounded-xl shadow-sm border border-slate-200">
        <Filter className="w-5 h-5 text-slate-400" />
        <select 
          value={filterStatus} 
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 outline-none"
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="paid">Paid</option>
        </select>
        <span className="text-sm text-slate-500">{filteredEarnings.length} entries</span>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Freelancer</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Task</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Week Start</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Amount</th>
                <th className="text-center px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Status</th>
                <th className="text-center px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredEarnings.length === 0 ? (
                <tr><td colSpan="6" className="px-6 py-8 text-center text-slate-500">No earnings found.</td></tr>
              ) : (
                filteredEarnings.map(e => {
                  const designer = e.expand?.designerId;
                  const task = e.expand?.taskId;
                  const weekStart = e.weekStart ? new Date(e.weekStart).toLocaleDateString('en-IN') : '—';
                  const statusColor = e.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700';
                  return (
                    <tr key={e.id} className="hover:bg-slate-50 transition">
                      <td className="px-6 py-3">{designer?.name || 'N/A'}</td>
                      <td className="px-6 py-3">{task?.title || 'N/A'}</td>
                      <td className="px-6 py-3">{weekStart}</td>
                      <td className="px-6 py-3 text-right font-mono text-indigo-600">₹{e.amount?.toFixed(2)}</td>
                      <td className="px-6 py-3 text-center">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColor}`}>
                          {e.status === 'paid' ? '✅ Paid' : '⏳ Pending'}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-center">
                        {e.status === 'pending' && (
                          <button 
                            onClick={() => handleMarkPaid(e.id)} 
                            className="px-3 py-1 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 transition flex items-center gap-1 mx-auto"
                          >
                            <CheckCircle className="w-4 h-4" /> Mark Paid
                          </button>
                        )}
                        {e.status === 'paid' && (
                          <span className="text-xs text-slate-400">Already paid</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}